import { Mukta, Open_Sans, Archivo } from "@next/font/google";

// CWV (Phase 2): self-host Google Fonts via next/font.
// - Eliminates the render-blocking <link> to fonts.googleapis.com (LCP win).
// - `adjustFontFallback` (on by default) auto-generates a metric-matched
//   fallback, so the font swap causes no layout shift — this REPLACES the
//   manual "Mukta-fallback"/"OpenSans-fallback" @font-face rules.
// - Each font exposes a CSS variable; SCSS/JS reference it via var(--font-*).
// Weights mirror what the old <link> loaded.

export const mukta = Mukta({
  subsets: ["latin"],
  weight: ["200", "300", "400", "500", "600"],
  display: "swap",
  variable: "--font-mukta",
});

export const openSans = Open_Sans({
  subsets: ["latin"],
  // Open Sans has no weight 200 (starts at 300); the old <link> requested 200
  // but Google silently dropped it. Weights here match actual usage in the app.
  weight: ["300", "400", "500", "600", "700"],
  display: "swap",
  variable: "--font-open-sans",
});

export const archivo = Archivo({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
  display: "swap",
  variable: "--font-archivo",
});

// Convenience: all three variable classes to spread onto the root element.
export const fontVariables = `${mukta.variable} ${openSans.variable} ${archivo.variable}`;

// --- CLS fix: inline critical font fallback (see _document.js) ---------------
// next/font generates metric-matched `size-adjust` @font-face fallbacks, but in
// Next 13.1.6 that CSS is bundled into the big render-blocking stylesheet, so it
// only applies AFTER first paint — causing the whole <body> to reflow when it
// finally loads (PDP desktop CLS up to ~1.1, race-dependent).
//
// To prevent that we inline our OWN identically-named-metric fallback @font-face
// rules + seed the --font-* variables in <head>, so a metric-matched Arial is in
// effect at first paint. When next/font's own CSS later loads it overrides the
// variables with its hashed stack — but since both fallbacks share the same
// Arial metrics, the swap is shift-free. Override numbers below are copied
// verbatim from next/font's generated output for these exact fonts/weights.
//
// CRITICAL: seeding the vars is not enough — we must also re-apply the SAME
// root-level rules that consume them, because those rules live in the big
// render-blocking CSS too. An audit of the served CSS found exactly TWO
// root-level font-family rules (everything else is class/component-scoped and
// covered transitively):
//   *    { font-family: var(--font-mukta), sans-serif }     (Tailwind reset)
//   html { font-family: var(--font-open-sans), sans-serif } (Tailwind preflight)
// We mirror BOTH here. `html` (specificity 0-0-1) must be covered explicitly or
// it would still paint in browser-default serif then swap to Open Sans when the
// big CSS lands (residual <body> shift). Same selectors + earlier source order
// => once the big CSS loads its identical rules win and .__variable_ swaps in
// the real hashed fonts, shift-free (same Arial metrics).
export const criticalFontFallbackCss = `
@font-face{font-family:'Mukta-cls-fallback';src:local('Arial');ascent-override:119.61%;descent-override:56.31%;line-gap-override:0.00%;size-adjust:94.47%}
@font-face{font-family:'OpenSans-cls-fallback';src:local('Arial');ascent-override:101.58%;descent-override:27.84%;line-gap-override:0.00%;size-adjust:105.22%}
@font-face{font-family:'Archivo-cls-fallback';src:local('Arial');ascent-override:88.86%;descent-override:21.25%;line-gap-override:0.00%;size-adjust:98.81%}
:root{--font-mukta:'Mukta-cls-fallback';--font-open-sans:'OpenSans-cls-fallback';--font-archivo:'Archivo-cls-fallback';}
*{font-family:var(--font-mukta),'Mukta-cls-fallback',sans-serif}
html{font-family:var(--font-open-sans),'OpenSans-cls-fallback',sans-serif}
`;
